import dayjs, { type Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import type { RobotScheduleEvent } from '../components/RobotSchedule/RobotSchedule.types';
import { DEFAULT_CLEANING_DURATION_MINUTES, DEFAULT_CHARGING_DURATION_MINUTES } from './constants';
import type { ScheduleOccurrence } from 'app/modules/machine-inventory/ScheduleClient';
import type { CleaningScheduleV2 } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

dayjs.extend(duration);

const MIN_MS = 60_000;
const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;

const range = (startInclusive: number, endExclusive: number): number[] =>
  Array.from({ length: Math.max(endExclusive - startInclusive, 0) }, (_, index) => startInclusive + index);

export const combineScheduleDateAndTime = (scheduleDate: Dayjs, scheduleTime: Dayjs): Dayjs =>
  scheduleDate.hour(scheduleTime.hour()).minute(scheduleTime.minute()).second(0).millisecond(0);

export const isScheduleDateTimeInThePast = (value: Dayjs | Date | string): boolean => !dayjs(value).isAfter(dayjs());

export const isDisabledPastScheduleDate = (current: Dayjs | null): boolean =>
  Boolean(current && current.isBefore(dayjs(), 'day'));

export const getDisabledPastScheduleTimes =
  (
    scheduleDate?: Dayjs | null
  ): (() => { disabledHours: () => number[]; disabledMinutes: (selectedHour: number) => number[] }) =>
  () => {
    const now = dayjs();

    if (!scheduleDate || scheduleDate.isAfter(now, 'day')) {
      return {
        disabledHours: () => [],
        disabledMinutes: () => [],
      };
    }

    if (scheduleDate.isBefore(now, 'day')) {
      return {
        disabledHours: () => range(0, HOURS_IN_DAY),
        disabledMinutes: () => range(0, MINUTES_IN_HOUR),
      };
    }

    return {
      disabledHours: () => range(0, now.hour()),
      disabledMinutes: (selectedHour: number): number[] => {
        if (selectedHour > now.hour()) {
          return [];
        }

        if (selectedHour < now.hour()) {
          return range(0, MINUTES_IN_HOUR);
        }

        return range(0, now.minute() + 1);
      },
    };
  };

/**
 * Duration fields on route/routine are ISO 8601 (e.g. "P0Y0M0DT0H15M0S"). Falls back when the
 * value is missing or invalid — this covers both an occurrence that only has a `map` (which has
 * no duration fields at all) and one with a route/routine whose duration isn't populated by the
 * backend yet.
 */
export const parseIso8601DurationMinutes = (
  value?: string | null,
  fallback = DEFAULT_CLEANING_DURATION_MINUTES
): number => {
  if (!value) {
    return fallback;
  }

  const minutes = dayjs.duration(value).asMinutes();

  return Number.isFinite(minutes) && minutes > 0 ? minutes : fallback;
};

export const formatDurationShort = (ms: number): string => {
  const totalMinutes = Math.round(ms / MIN_MS);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes}m`;
  }

  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

export const getOccurrenceDisplayName = (occurrence: ScheduleOccurrence): string =>
  occurrence.scheduleName || occurrence.routine?.name || occurrence.route?.name || occurrence.map?.name || '-';

export const mapOccurrenceToScheduleEvent = ({
  occurrence,
  isB50,
  schedule,
}: {
  occurrence: ScheduleOccurrence;
  isB50: boolean;
  schedule?: CleaningScheduleV2;
}): RobotScheduleEvent => {
  const cleaningDurationMinutes = parseIso8601DurationMinutes(
    occurrence.routine?.duration ?? occurrence.route?.duration,
    DEFAULT_CLEANING_DURATION_MINUTES
  );
  // CV robots have no post-charging time — only B50 robots dock to charge after cleaning.
  const chargingDurationMinutes = isB50
    ? parseIso8601DurationMinutes(
        occurrence.routine?.postChargingDuration ?? occurrence.route?.postChargingDuration,
        DEFAULT_CHARGING_DURATION_MINUTES
      )
    : 0;
  const startAt = occurrence.startDate;
  const endAt = dayjs(startAt).add(cleaningDurationMinutes, 'minute').toISOString();

  return {
    id: occurrence.scheduleId,
    name: schedule?.name ?? getOccurrenceDisplayName(occurrence),
    startAt,
    endAt,
    chargingDurationMs: chargingDurationMinutes * MIN_MS,
    occurrence,
  };
};
